import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ResumerBuilderSetupComponent } from '../resumer-builder-setup/resumer-builder-setup.component';
import { ResumerBuilderEducationComponent } from '../resumer-builder-education/resumer-builder-education.component';
import { ResumerBuilderPersonalComponent } from '../resumer-builder-personal/resumer-builder-personal.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ResumerBuilderSkillsComponent } from '../resumer-builder-skills/resumer-builder-skills.component';
import { ResumerBuilderProjectsComponent } from '../resumer-builder-projects/resumer-builder-projects.component';
import { ResumerBuilderExperienceComponent } from '../resumer-builder-experience/resumer-builder-experience.component';
import { ResumerBuilderExtrasComponent } from '../resumer-builder-extras/resumer-builder-extras.component';
import { ResumerBuilderSummaryComponent } from '../resumer-builder-summary/resumer-builder-summary.component';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ResumeDataByID } from '@edtech-ui/core/models/resume-builder/get-resume-by-id';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
@Component({
	selector: 'app-resumer-builder',
	standalone: true,
	imports: [CommonModule,
		ResumerBuilderSetupComponent, ResumerBuilderEducationComponent,
		ResumerBuilderPersonalComponent, ResumerBuilderSkillsComponent,
		ResumerBuilderProjectsComponent, ResumerBuilderExperienceComponent,
		ResumerBuilderExtrasComponent, ResumerBuilderSummaryComponent,
		RouterModule, HasPermissionDirective
	],
	templateUrl: './resumer-builder.component.html',
	styleUrl: './resumer-builder.component.scss'
})
export class ResumerBuilderComponent implements OnInit, OnDestroy {

	@ViewChild(ResumerBuilderSetupComponent)
	setupComponent!: ResumerBuilderSetupComponent;

	@ViewChild(ResumerBuilderPersonalComponent)
	personalComponent!: ResumerBuilderPersonalComponent;

	@ViewChild(ResumerBuilderEducationComponent)
	educationComponent!: ResumerBuilderEducationComponent

	@ViewChild(ResumerBuilderSkillsComponent)
	skillsComponent!: ResumerBuilderSkillsComponent

	@ViewChild(ResumerBuilderProjectsComponent)
	projectComponent!: ResumerBuilderProjectsComponent

	@ViewChild(ResumerBuilderExperienceComponent)
	experienceComponent!: ResumerBuilderExperienceComponent;

	@ViewChild(ResumerBuilderExtrasComponent)
	extrasComponent!: ResumerBuilderExtrasComponent;

	@ViewChild(ResumerBuilderSummaryComponent)
	summaryComponent!: ResumerBuilderSummaryComponent;

	subscription = new Subscription();
	resumeId: string = '';
	targetRole: string = '';
	currentStep = 0;
	completedSteps: number[] = [];
	isLoading = false;
	returnUrl: string | null = null;
	studentResumeDetails: ResumeDataByID | null = null;
	PermissionForStudents = PermissionForStudents;
	isEditMode: boolean = false;

	constructor(
		private router: Router,
		private _resumeService: ResumeService,
		private _toastr: ToastrService,
		private route: ActivatedRoute

	) { }


	ngOnInit(): void {
		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				if (params['returnUrl']) {
					this.returnUrl = params['returnUrl'];
				}
			})
		);

		this.subscription.add(
			this.route.paramMap.subscribe(params => {
				const resumeId = params.get('resumeId');
				if (resumeId) {
					this.resumeId = resumeId;
					this.isEditMode = true; 
					this._resumeService.setResumeId(this.resumeId);
					this.getStudentResumes();
				} else {
					this.isEditMode = false; 
					this.resumeId = '';
					this._resumeService.resetResumeBuilder();
				}
			})
		);
	}

	steps = [
		{ label: 'Set Up', icon: 'bi-gear-fill' },
		{ label: 'Personal', icon: 'bi-person-fill' },
		{ label: 'Education', icon: 'bi-mortarboard-fill' },
		{ label: 'Skills', icon: 'bi-lightning-charge-fill' },
		{ label: 'Projects', icon: 'bi-kanban-fill' },
		{ label: 'Experiences', icon: 'bi-briefcase-fill' },
		{ label: 'Extras', icon: 'bi-stars' },
		{ label: 'Summary', icon: 'bi-file-earmark-text-fill' }
	];


	goToStep(index: number) {
		if (this.currentStep === index) return;

		// Save current step data before navigating
		if (this.currentStep === 0) {
			if (!this.isCurrentStepValid()) {
				this._toastr.error('Please fill required fields in Set Up');
				if (this.setupComponent?.resumeForm) {
					this.setupComponent.resumeForm.markAllAsTouched();
				}
				return;
			}

			const data = this.setupComponent.getFormData();
			const userId = localStorage.getItem('user_id');

			if (!this.resumeId) {
				const formData = new FormData();
				formData.append('target_role', data.target_role || '');
				formData.append('template_id', data.template_id);
				formData.append('theme_color', data.theme_color);
				formData.append('user_id', userId || '');

				if (data.file) {
					formData.append('file', data.file);
				}

				if (data.experience_level) {
					formData.append('experience_level', data.experience_level || 'Fresher');
				}
				this.isLoading = true;

				this.subscription.add(
					this._resumeService.postStudentResume(formData).pipe(
						tap(response => {
							if (response.Success) {
								this.resumeId = response.Data.resume_id;
								this._resumeService.setResumeId(this.resumeId);
								this._resumeService.targetRole.next(response.Data.targetRole);
								if (response.Data.config?.photoUrl) {
									this.setupComponent.previewUrl = response.Data.config.photoUrl;
									this.setupComponent.selectedFile = null;
								}
								this.currentStep = index;
								this.updateCompletedSteps(index);
							} else {
								this._toastr.error(response?.Message);
							}
						}),
						finalize(() => {
							this.isLoading = false;
						}),
						catchError(() => {
							this._toastr.error('Something went wrong');
							return of(null);
						})
					).subscribe()
				);
				return;
			}

			const photoUrlToSaveInGoTo = (typeof this.setupComponent.previewUrl === 'string' && this.setupComponent.previewUrl.startsWith('http')) 
				? this.setupComponent.previewUrl 
				: undefined;

			const payloadGoTo: any = {
				targetRole: data.target_role,
				experience_level: data.experience_level,
				config: {
					templateId: data.template_id,
					themeColor: data.theme_color,
					includePhoto: this.setupComponent.includePhoto,
					photoUrl: photoUrlToSaveInGoTo
				}
			};

			if (data.file) {
				payloadGoTo.file = data.file;
			}

			this.isLoading = true;

			this.subscription.add(
				this._resumeService.updateStudentResume(this.resumeId, payloadGoTo).pipe(
					tap(res => {
						if (res?.Success && res?.Data?.config?.photoUrl) {
							this.setupComponent.previewUrl = res.Data.config.photoUrl;
							this.setupComponent.selectedFile = null;
						}
						this.currentStep = index;
						this.updateCompletedSteps(index);
					}),
					finalize(() => {
						this.isLoading = false;
					}),
					catchError(() => {
						this._toastr.error('Update failed');
						return of(null);
					})
				).subscribe()
			);
			return;
		}

		if (!this.isCurrentStepValid()) {
			this._toastr.error('Please fill required fields');
			this.markCurrentStepAsTouched();
			return;
		}

		if (this.currentStep === 1) this.personalComponent.saveData();
		else if (this.currentStep === 2) this.educationComponent.saveData();
		else if (this.currentStep === 3) this.skillsComponent.saveData();
		else if (this.currentStep === 4) this.projectComponent.saveData();
		else if (this.currentStep === 5) this.experienceComponent.saveData();
		else if (this.currentStep === 6) this.extrasComponent.saveData();
		else if (this.currentStep === 7) this.summaryComponent.saveData();

		this.currentStep = index;
		this.updateCompletedSteps(index);
	}

	markCurrentStepAsTouched() {
		const stepMap: any = {
			0: this.setupComponent?.resumeForm,
			1: this.personalComponent?.userProfileForm,
			2: this.educationComponent?.userProfileForm,
			4: this.projectComponent?.userProfileForm,
			5: this.experienceComponent?.userProfileForm,
			6: this.extrasComponent?.userForm
		};

		const form = stepMap[this.currentStep];
		if (form && typeof form.markAllAsTouched === 'function') {
			form.markAllAsTouched();
		}
	}

	updateCompletedSteps(index: number) {
		this.completedSteps = [];
		for (let i = 0; i < index; i++) {
			this.completedSteps.push(i);
		}
	}

	nextStep() {

		if (!this.isCurrentStepValid()) {
			this._toastr.error('Please fill required fields');
			return;
		}

		if (this.currentStep === 0) {
			const data = this.setupComponent.getFormData();

			if (!this.resumeId) {
				const data = this.setupComponent.getFormData();
				const userId = localStorage.getItem('user_id');

				const formData = new FormData();

				formData.append('target_role', data.target_role || '');
				formData.append('template_id', data.template_id);
				formData.append('theme_color', data.theme_color);
				formData.append('user_id', userId || '');

				if (data.file) {
					formData.append('file', data.file);
				}

				if (data.experience_level) {
					formData.append('experience_level', data.experience_level || 'Fresher');
				}
				this.isLoading = true;

				this.subscription.add(
					this._resumeService.postStudentResume(formData).pipe(
						tap(response => {
							if (response.Success) {
								this.resumeId = response.Data.resume_id;
								this._resumeService.setResumeId(this.resumeId);
								this._resumeService.targetRole.next(response.Data.targetRole);
								if (response.Data.config?.photoUrl) {
									this.setupComponent.previewUrl = response.Data.config.photoUrl;
									this.setupComponent.selectedFile = null;
								}
								this.completedSteps.push(this.currentStep);
								this.currentStep++;
							} else {
								this._toastr.error(response?.Message);
							}
						}),
						finalize(() => {
							this.isLoading = false;
						}),
						catchError(() => {
							this._toastr.error('Something went wrong');
							return of(null);
						})
					).subscribe()
				)
				return;
			}

			const photoUrlToSaveInNext = (typeof this.setupComponent.previewUrl === 'string' && this.setupComponent.previewUrl.startsWith('http')) 
				? this.setupComponent.previewUrl 
				: undefined;

			const payloadNext: any = {
				targetRole: data.target_role,
				experience_level: data.experience_level,
				config: {
					templateId: data.template_id,
					themeColor: data.theme_color,
					includePhoto: this.setupComponent.includePhoto,
					photoUrl: photoUrlToSaveInNext
				}
			};

			if (data.file) {
				payloadNext.file = data.file;
			}

			this.isLoading = true;

			this.subscription.add(
				this._resumeService.updateStudentResume(this.resumeId, payloadNext).pipe(
					tap(res => {
						if (res?.Success && res?.Data?.config?.photoUrl) {
							this.setupComponent.previewUrl = res.Data.config.photoUrl;
							this.setupComponent.selectedFile = null;
						}
						this.completedSteps.push(this.currentStep);
						this.currentStep++;
					}),
					finalize(() => {
						this.isLoading = false;
					}),
					catchError(() => {
						this._toastr.error('Update failed');
						return of(null);
					})
				).subscribe()
			);

			return;
		}

		if (this.currentStep === 1) {
			this.personalComponent.saveData();
			this.completedSteps.push(this.currentStep);
			this.currentStep++;
			return;
		}

		if (this.currentStep === 2) {
			this.educationComponent.saveData();
			this.completedSteps.push(this.currentStep);
			this.currentStep++;
			return;
		}


		if (this.currentStep === 3) {
			this.skillsComponent.saveData();
			this.completedSteps.push(this.currentStep);
			this.currentStep++;
			return;
		}

		if (this.currentStep === 4) {
			this.projectComponent.saveData();
			this.completedSteps.push(this.currentStep);
			this.currentStep++;
			return;
		}

		if (this.currentStep === 5) {
			this.experienceComponent.saveData();
			this.completedSteps.push(this.currentStep);
			this.currentStep++;
			return;
		}


		if (this.currentStep === 6) {
			this.extrasComponent.saveData();
			this.completedSteps.push(this.currentStep);
			this.currentStep++;
			return;
		}

		if (this.currentStep === 7) {
			this.summaryComponent.saveData();
			this.completedSteps.push(this.currentStep);
			this.onCompleteResume();
			return;
		}
	}

	onCompleteResume() {
		this.isLoading = true;

		const resumeId = this._resumeService.getResumeId();
		const allData = this._resumeService.getAllData();

		this.subscription.add(
			this._resumeService.updateStudentResume(resumeId, allData).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success('Resume saved successfully');
						const navExtras: any = {};
						if (this.returnUrl) {
							navExtras.queryParams = { returnUrl: this.returnUrl };
						}
						this.router.navigate(['/student-dashboard/resume-preview', resumeId], navExtras);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		);
	}


	prevStep() {
		if (this.currentStep > 0) {
			this.currentStep--;
		}
	}

	isCompleted(index: number): boolean {
		return this.completedSteps.includes(index);
	}

	isLastStep(): boolean {
		return this.currentStep === this.steps.length - 1;
	}


	getStudentResumes() {
		this.subscription.add(
			this._resumeService.getStudentResumeById(this.resumeId).pipe(
				tap(response => {
					if (response.Success) {
						this.studentResumeDetails = response.Data;
						this._resumeService.setFullResumeData(response.Data);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		)
	}


	isCurrentStepValid(): boolean {
		const stepMap: any = {
			0: this.setupComponent,
			1: this.personalComponent,
			2: this.educationComponent,
			3: this.skillsComponent,
			4: this.projectComponent,
			5: this.experienceComponent,
			6: this.extrasComponent,
			7: this.summaryComponent
		};

		return stepMap[this.currentStep]?.isFormValid?.() ?? true;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}